
import React, { useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Copy, Check, RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { UseFormReturn } from 'react-hook-form';
import { UserAssignmentFormValues } from './types';

interface PasswordFieldProps {
  form: UseFormReturn<UserAssignmentFormValues>;
}

export const PasswordField = ({ form }: PasswordFieldProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const regeneratePassword = () => {
    form.setValue('password', generateStrongPassword());
    setCopied(false);
  };

  const copyPasswordToClipboard = () => {
    const password = form.getValues('password');
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      toast({
        title: "Password copied",
        description: "The password has been copied to your clipboard",
      });
      
      // Reset copy status after 3 seconds
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <FormField
      control={form.control}
      name="password"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Generated Password <span className="text-red-500">*</span></FormLabel>
          <div className="text-sm text-muted-foreground mb-2">
            This password will be used to create the user account with the Strategy Provider role
          </div>
          <div className="flex space-x-2">
            <div className="relative flex-grow">
              <FormControl>
                <Input 
                  {...field}
                  type="text" 
                  readOnly
                  className="pr-10"
                />
              </FormControl>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={copyPasswordToClipboard}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  <span className="sr-only">Copy password</span>
                </Button>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={regeneratePassword}
              className="h-10 w-10"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="sr-only">Generate new password</span>
            </Button>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Make sure to copy this password! You won't be able to see it again.
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

function generateStrongPassword(length = 12) {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
}
